import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../Components/Toast';
import { connectMeta, getMetaAuthUrl } from '../../services/metaAdsService';
import {
  getMetaErrorMessage,
  getMetaRedirectUriParam,
  getMetaWarningMessages,
  isMetaRedirectUriRejected,
  resolveMetaConnectAction,
} from '../metaAdsConfig';
import { markMetaWriteReturn } from '../metaWriteReturn';
import { META_WRITE_PERMISSION_MESSAGE } from '../metaWrite';

const defaultRedirect = (url) => window.location.assign(url);
const ERROR_TITLE = 'No pudimos conectar Meta';

/**
 * Pide `GET /getmetaauthurl`. Fuera del dominio por defecto (localhost) manda
 * `redirect_uri`; si la API lo rechaza (400 `VALIDATION_ERROR`, `field:
 * "redirect_uri"`) reintenta una vez sin él.
 */
async function requestAuthUrl(access = null) {
  const redirectUri = getMetaRedirectUriParam();
  const extra = access ? { access } : {};
  const res = await getMetaAuthUrl(redirectUri ? { redirectUri, ...extra } : extra);
  if (redirectUri && !res?._httpOk && isMetaRedirectUriRejected(res)) return access ? getMetaAuthUrl(extra) : getMetaAuthUrl();
  return res;
}

/**
 * Acción «Conectar con Meta» (solo dueño). Decide con `resolveMetaConnectAction`:
 * - `mode: "live"`: redirige en la misma pestaña a `auth_url` (facebook.com);
 *   Meta vuelve a `/meta-callback` (ver `Pages/MetaCallbackPage`).
 * - `mode: "fixture"`: redirige a `auth_url` (vuelve al callback de este
 *   Terrio con `code=fixture-code`), o manda `{ code: fixture_code, state }` a
 *   `POST /connectmeta` sin salir (flag de localhost, `auth_url` null u otro origen).
 * `already_connected: true` (200) también es éxito.
 * F2a: `connect({ access: 'write' })` pide el login «con escritura» (`getmetaauthurl?access=write`);
 * antes de ir a Meta marca la vuelta para que el panel abra ⚙. Sin argumento (o con el evento del
 * botón), todo igual que antes.
 *
 * @param {{ onConnected?: (data: object) => void, redirect?: (url: string) => void }} [options]
 * @returns {{ connecting: boolean, connect: () => Promise<void> }}
 */
export function useMetaConnect({ onConnected, redirect = defaultRedirect } = {}) {
  const [connecting, setConnecting] = useState(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const connect = useCallback(async (options) => {
    if (busyRef.current) return;
    const access = options?.access === 'write' ? 'write' : null;
    const finish = () => {
      busyRef.current = false;
      if (mountedRef.current) setConnecting(false);
    };
    busyRef.current = true;
    setConnecting(true);

    const urlRes = await requestAuthUrl(access);
    if (!urlRes?._httpOk) {
      toast.error({ title: ERROR_TITLE, message: getMetaErrorMessage(urlRes) });
      finish();
      return;
    }

    const action = resolveMetaConnectAction(urlRes.data);
    if (action.kind === 'redirect') {
      // Sale de esta pantalla: el botón queda en «Conectando…» hasta que navega.
      if (access) markMetaWriteReturn();
      redirect(action.url);
      return;
    }
    if (action.kind === 'invalid') {
      toast.error({ title: ERROR_TITLE, message: getMetaErrorMessage({ _httpStatus: 500 }) });
      finish();
      return;
    }

    const res = await connectMeta({ code: action.code, state: action.state });
    if (res?._httpOk) {
      onConnected?.(res.data);
      toast.success({ message: 'Meta conectado (modo demo).' });
      getMetaWarningMessages(res).forEach((message) => toast.info({ message }));
    } else if (access && res?.code === 'META_PERMISSION_MISSING') {
      // No se guardó nada: la conexión de solo lectura sigue igual.
      toast.error({ title: 'Falta el permiso de Meta', message: META_WRITE_PERMISSION_MESSAGE });
    } else {
      toast.error({ title: ERROR_TITLE, message: getMetaErrorMessage(res) });
    }
    finish();
  }, [onConnected, redirect]);

  return { connecting, connect };
}
